<template>
  <div class="min-h-screen max-w-screen-2xl mx-auto bg-black text-white font-sans">
    <!-- Hero and Frame Carousel Section -->
    <section class="relative min-h-screen flex flex-col md:flex-row items-center justify-center overflow-hidden mt-16">
      <!-- Hero Content (1/3 width on desktop) -->
      <div class="w-full md:w-1/3 px-4 md:px-8 lg:px-12 xl:px-24 py-12 md:py-0 text-center md:text-left">
        <h1 class="text-4xl md:text-4xl lg:text-5xl font-bold mb-8 tracking-tight lowercase">
          ploppin'
        </h1>
        <p class="text-lg md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto md:mx-0 mb-12">
          manga. selfies. portraits. <br/><b>anything.</b> <br/> transform into irl experience.
        </p>
        
        <!-- Main CTA Button -->
        <button 
          @click="handleFreeTier"
          class="px-8 py-3 text-lg lg:text-2xl lg:px-12 lg:py-5 font-medium border-2 border-white hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
        >
          get started
        </button>
      </div>

      <!-- Frame Carousel (2/3 width on desktop) -->
      <div class="w-full md:w-2/3 px-4 md:px-8 py-12 md:py-0">
        <div class="relative max-w-6xl mx-auto">
          <!-- Frame Container -->
          <div class="relative aspect-[16/10] bg-gray-900 rounded-2xl overflow-hidden">
            <!-- Loading Shimmer -->
            <div v-if="demoLoading || !framesReady" class="absolute inset-0 bg-gray-800 animate-pulse"></div>
            
            <!-- Preload all frames (hidden) -->
            <div class="hidden">
              <img 
                v-for="(frameUrl, index) in outputFramesUrls" 
                :key="index"
                :src="frameUrl"
                @load="onFrameLoaded(index)"
                @error="onFrameError(index)"
              />
            </div>
            
            <!-- Main frame display -->
            <img
              v-if="currentFrameUrl"
              :src="currentFrameUrl"
              class="w-full h-full object-cover grayscale"
              :alt="`Frame ${currentFrameIndex + 1}`"
            />
            
            <!-- Loading indicator for frames -->
            <div v-if="!framesReady" class="absolute inset-0 flex items-center justify-center">
              <div class="bg-black/30 backdrop-blur-md px-6 py-3 rounded-xl border border-white/10">
                <div class="text-gray-200">Loading frames... {{ loadedFramesCount }}/{{ totalFrames }}</div>
              </div>
            </div>
            
            <!-- Navigation Arrows -->
            <button 
              @click="previousDemo"
              class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-left"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            
            <button 
              @click="nextDemo"
              class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            
            <!-- Responsive Frame Slider -->
            <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2">
              <!-- Mobile Linear Slider (visible on small screens) -->
              <div 
                ref="mobileSliderContainer"
                class="xl:hidden relative w-64 h-8 bg-black/40 backdrop-blur-sm rounded-lg overflow-hidden border border-white/20 cursor-pointer"
                @mousedown="startLinearDragging"
                @touchstart="startLinearDragging"
              >
                <!-- Progress Overlay -->
                <div 
                  class="absolute bottom-0 left-0 h-1 bg-white"
                  :style="{ width: `${(currentFrameIndex / (totalFrames - 1)) * 100}%` }"
                ></div>

                <!-- Current Position Indicator -->
                <div 
                  class="absolute top-1/2 h-4 w-4 bg-white rounded-full transform -translate-y-1/2 -translate-x-1/2 cursor-pointer shadow-[0_0_15px_rgba(255,255,255,0.5)]"
                  :style="{ left: `${(currentFrameIndex / (totalFrames - 1)) * 100}%` }"
                ></div>
              </div>

              <!-- Desktop Square Slider (hidden on small screens) -->
              <div 
                ref="sliderContainer"
                class="hidden xl:block relative w-32 h-32 bg-black/40 backdrop-blur-sm rounded-lg overflow-hidden border border-white/20 cursor-pointer"
                @mousedown="startDragging"
                @touchstart="startDragging"
              >
                <!-- Miniature Frame Preview -->
                <div class="absolute inset-0 overflow-hidden opacity-50">           
                  <img
                    v-if="currentFrameUrl"
                    :src="currentFrameUrl"
                    class="w-full h-full object-cover grayscale"
                    :alt="`Frame ${currentFrameIndex + 1}`"
                  />
                </div>

                <!-- Slider Grid Lines -->
                <div class="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
                  <div v-for="i in 9" :key="i" class="border border-white/10"></div>
                </div>
                
                <!-- Current Position Indicator -->
                <div 
                  class="absolute w-4 h-4 bg-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 z-20"
                  :style="{ 
                    left: `${sliderPosition.x}%`, 
                    top: `${sliderPosition.y}%`,
                    boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                  }"
                ></div>

                <!-- Progress Overlay -->
                <div 
                  class="absolute bottom-0 left-0 h-1 bg-white"
                  :style="{ width: `${(currentFrameIndex / (totalFrames - 1)) * 100}%` }"
                ></div>
              </div>
            </div>
          </div>
          
          <!-- Demo Info -->
          <div class="mt-6 text-center">
            <h3 class="text-lg font-medium lowercase">{{ currentDemo.title }}</h3>
            <p class="text-gray-400 text-sm mt-1 lowercase">{{ currentDemo.tags.join(' · ') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="container max-w-5xl mx-auto px-4 py-20">
      <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-16 lowercase">how it works</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-white flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-upload"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
          </div>
          <h3 class="text-xl font-bold mb-3 lowercase">upload</h3>
          <p class="text-gray-400 lowercase">drop any image and crop it.</p>
        </div>
        
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-white flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-wand-2"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z"/><path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/></svg>
          </div>
          <h3 class="text-xl font-bold mb-3 lowercase">transform</h3>
          <p class="text-gray-400 lowercase">ai does its magic while you wait.</p>
        </div>
        
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-white flex items-center justify-center">
            <!-- Improved download icon that better represents "ploppin'" -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-layers-3"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>
          </div>
          <h3 class="text-xl font-bold mb-3 lowercase">ploppin'</h3>
          <p class="text-gray-400 lowercase">feel the image in 3d view.</p>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-4 md:py-20 relative z-[2]">
      <div class="max-w-6xl mx-auto px-4 text-center">
        <!-- Smaller headings on mobile -->
        <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4 lowercase">
          choose your plan
        </h2>
        <p class="text-sm md:text-base text-gray-400 mb-6 md:mb-12 lowercase">
          transform your images into irl 3d experiences.
        </p>
        
        <!-- Grid layout with items aligned to stretch and flex-start -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8 md:items-start">
          <!-- Basic Tier -->
          <div class="relative bg-gray-800/50 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-8 border border-white/20 hover:border-white/40 transition-all group h-auto">
            <div class="space-y-2 md:space-y-4">
              <h3 class="text-sm md:text-lg font-medium text-gray-400 lowercase">basic</h3>
              <div class="text-2xl md:text-4xl font-bold">
                $0 <span class="text-xs md:text-lg font-normal text-gray-400">/month</span>
              </div>
              <p class="text-sm md:text-base text-gray-400 lowercase">try it out.</p>
              
              <div class="space-y-2 md:space-y-3 pt-2 md:pt-4">
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <Gem class="w-5 h-4 text-white" />
                  <span>5 credits</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>basic effects</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  <span>7 day deletion</span>
                </div>
              </div>
            </div>
            <button 
              class="w-full mt-4 md:mt-8 py-2 md:py-3 rounded-xl text-sm md:text-base bg-white/10 hover:bg-white/20 transition-colors"
              @click="handleFreeTier"
            >
              Get Started
            </button>
          </div>

          <!-- Plus Tier -->
          <div class="relative bg-gradient-to-b from-gray-800 to-gray-900/10 rounded-xl md:rounded-2xl p-4 md:p-8 border border-white/30 hover:border-white/50 transition-all group transform hover:-translate-y-1 md:h-auto">
            <div class="relative space-y-2 md:space-y-4">
              <h3 class="text-sm md:text-lg font-medium text-white lowercase">plus</h3>
              <div class="text-2xl md:text-4xl font-bold">
                $14 <span class="text-xs md:text-lg font-normal text-gray-400">/month</span>
              </div>
              <p class="text-sm md:text-base text-gray-400 lowercase">ploppin' one.</p>
              
              <div class="space-y-2 md:space-y-3 pt-2 md:pt-4">
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <Gem class="w-5 h-4 text-white" />
                  <span>240 credits / month</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>premium effects</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>cloud storage</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>no watermark</span>
                </div>
              </div>
            </div>
            <button 
              class="w-full mt-4 md:mt-8 py-2 md:py-3 rounded-xl text-sm md:text-base bg-white hover:bg-gray-100 text-black font-medium transition-colors"
              :disabled="checkoutLoading"
              @click="handleCheckout('plus')"
            >
              <template v-if="checkoutLoading && activeCheckoutProductType === 'plus'">
                <svg class="animate-spin h-5 w-5 inline mr-2" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Processing...
              </template>
              <template v-else>
                Choose Plus
              </template>
            </button>
          </div>

          <!-- Pro Tier -->
          <div class="relative bg-gradient-to-b from-gray-900 to-black/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-8 border border-white/20 hover:border-white/40 transition-all group h-auto">
            <div class="space-y-2 md:space-y-4">
              <h3 class="text-sm md:text-lg font-medium text-gray-400 lowercase">pro</h3>
              <div class="text-2xl md:text-4xl font-bold">
                $69 <span class="text-xs md:text-lg font-normal text-gray-400">/month</span>
              </div>
              <p class="text-sm md:text-base text-gray-400 lowercase">for power users.</p>
              
              <div class="space-y-2 md:space-y-3 pt-2 md:pt-4">
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <Gem class="w-5 h-4 text-white" />
                  <span>1400 credits / month</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>plus tier benefits</span>
                </div>
                <div class="flex items-center gap-1.5 md:gap-2 text-gray-300 text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span>priority access</span>
                </div>
              </div>
            </div>
            <button 
              class="w-full mt-4 md:mt-8 py-2 md:py-3 rounded-xl text-sm md:text-base bg-white/10 hover:bg-white/20 transition-colors"
              :disabled="checkoutLoading"
              @click="handleCheckout('pro')"
            >
              <template v-if="checkoutLoading && activeCheckoutProductType === 'pro'">
                <svg class="animate-spin h-5 w-5 inline mr-2" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Processing...
              </template>
              <template v-else>
                Choose Pro
              </template>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section id="faq" class="py-8 md:py-20 relative z-[2]">
      <div class="max-w-4xl mx-auto px-4">
        <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-12 text-center lowercase">frequently asked questions</h2>
        
        <div class="space-y-2 md:space-y-4">
          <!-- Commercial Usage -->
          <div 
            class="relative bg-gray-800/50 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-6 border border-white/20 hover:border-white/30 transition-all cursor-pointer"
            @click="toggleFaq(0)"
          >
            <div class="flex justify-between items-start gap-3">
              <h3 class="text-sm md:text-lg font-medium text-gray-200 lowercase">can i use this commercially?</h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="w-4 h-4 md:w-5 md:h-5 text-gray-400 transform transition-transform duration-200 flex-shrink-0"
                :class="{ 'rotate-180': openFaq === 0 }"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
            <div 
              class="overflow-hidden transition-all duration-200"
              :class="openFaq === 0 ? 'max-h-32 pt-2 md:pt-4' : 'max-h-0'"
            >
              <p class="text-xs md:text-base text-gray-400 lowercase">
                yes! you can use generated ploppin' videos for any commercial purposes.
              </p>
            </div>
          </div>

          <!-- Free Usage -->
          <div 
            class="relative bg-gray-800/50 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-6 border border-white/20 hover:border-white/30 transition-all cursor-pointer"
            @click="toggleFaq(1)"
          >
            <div class="flex justify-between items-start gap-3">
              <h3 class="text-sm md:text-lg font-medium text-gray-200 lowercase">is there a free tier?</h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="w-4 h-4 md:w-5 md:h-5 text-gray-400 transform transition-transform duration-200 flex-shrink-0"
                :class="{ 'rotate-180': openFaq === 1 }"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
            <div 
              class="overflow-hidden transition-all duration-200"
              :class="openFaq === 1 ? 'max-h-32 pt-2 md:pt-4' : 'max-h-0'"
            >
              <p class="text-xs md:text-base text-gray-400 lowercase">
                absolutely! we provide 5 free credits initially. this lets you experience 3d transformations without any commitment.
              </p>
            </div>
          </div>

          <!-- NSFW Content -->
          <!-- <div 
            class="relative bg-gray-800/50 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-6 border border-white/20 hover:border-white/30 transition-all cursor-pointer"
            @click="toggleFaq(2)"
          >
            <div class="flex justify-between items-start gap-3">
              <h3 class="text-sm md:text-lg font-medium text-gray-200 lowercase">is nsfw content allowed?</h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="w-4 h-4 md:w-5 md:h-5 text-gray-400 transform transition-transform duration-200 flex-shrink-0"
                :class="{ 'rotate-180': openFaq === 2 }"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
            <div 
              class="overflow-hidden transition-all duration-200"
              :class="openFaq === 2 ? 'max-h-32 pt-2 md:pt-4' : 'max-h-0'"
            >
              <p class="text-xs md:text-base text-gray-400 lowercase">
                while we don't explicitly prohibit nsfw content, any content that violates laws or our terms of service is not allowed. please use responsibly.
              </p>
            </div>
          </div> -->

          <!-- Export Options -->
          <div 
            class="relative bg-gray-800/50 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-6 border border-white/20 hover:border-white/30 transition-all cursor-pointer"
            @click="toggleFaq(3)"
          >
            <div class="flex justify-between items-start gap-3">
              <h3 class="text-sm md:text-lg font-medium text-gray-200 lowercase">what are the export options?</h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="w-4 h-4 md:w-5 md:h-5 text-gray-400 transform transition-transform duration-200 flex-shrink-0"
                :class="{ 'rotate-180': openFaq === 3 }"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </div>
            <div 
              class="overflow-hidden transition-all duration-200"
              :class="openFaq === 3 ? 'max-h-32 pt-2 md:pt-4' : 'max-h-0'"
            >
              <p class="text-xs md:text-base text-gray-400 lowercase">
                currently, we only support mp4 video export.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Secondary CTA -->
    <section class="pt-8 pb-20 text-center">
      <div class="max-w-3xl mx-auto px-4">
        <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 lowercase">ready to get started?</h2>
        <p class="text-xl text-gray-400 mb-10 lowercase">check it out for free. upgrade for premium.</p>
        <button 
          @click="handleFreeTier"
          class="px-12 py-4 text-xl font-medium border-2 border-white hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 lowercase"
        >
          try it now
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useAuth } from '~/composables/useAuth';
import { useRoute } from 'vue-router';
import { Gem } from 'lucide-vue-next';
import { useDemo } from '~/composables/useDemo';

const { isAuthenticated } = useAuth();
const route = useRoute();
const { createCheckout, loading: checkoutLoading, error: checkoutError } = usePolarCheckout();
const activeCheckoutProductType = ref(null);

// Performance detection
const isLowEndDevice = ref(false);
const isMobile = ref(false);

// Demo data
const demos = [
  {
    name: "manga-demo",
    title: "manga panels in motion",
    tags: ["manga", "animation"]
  },
  {
    name: "portrait-demo",
    title: "rotating portraits",
    tags: ["portrait", "3d effect"]
  },
  {
    name: "figure-demo",
    title: "collectible action figure",
    tags: ["action figure", "rotation"]
  },
  {
    name: "group-demo",
    title: "group pics reimagined",
    tags: ["photos", "memories"]
  }
];

const currentDemoIndex = ref(0);
const currentDemo = ref(demos[0]);

// Create a computed ref for the current demo name to pass to useDemo
const currentDemoName = computed(() => currentDemo.value.name);

const { demo, loading: demoLoading, error: demoError, outputFramesUrls } = useDemo(currentDemoName);

// Frame loading state
const framesReady = ref(false);
const loadedFramesCount = ref(0);
const currentFrameIndex = ref(0);
const totalFrames = ref(0);
const currentFrameUrl = ref('');
const loadedFrames = ref(new Set());
const frameErrors = ref(new Set());
const loadingPromises = ref([]);

// Slider state
const sliderContainer = ref(null);
const mobileSliderContainer = ref(null);
const sliderPosition = ref({ x: 50, y: 50 });
const isDragging = ref(false);
const lastUpdateTime = ref(0);
const UPDATE_THROTTLE = 10; // ms between updates

// Handle demo navigation
const nextDemo = () => {
  currentDemoIndex.value = (currentDemoIndex.value + 1) % demos.length;
  currentDemo.value = demos[currentDemoIndex.value];
  resetFrameState();
};

const previousDemo = () => {
  currentDemoIndex.value = (currentDemoIndex.value - 1 + demos.length) % demos.length;
  currentDemo.value = demos[currentDemoIndex.value];
  resetFrameState();
};

const resetFrameState = () => {
  framesReady.value = false;
  loadedFramesCount.value = 0;
  currentFrameIndex.value = 0;
  loadedFrames.value.clear();
  frameErrors.value.clear();
  loadingPromises.value = [];
};

// Frame loading functions
const onFrameLoaded = (index) => {
  loadedFrames.value.add(index);
  loadedFramesCount.value = loadedFrames.value.size;
  
  if (loadedFramesCount.value === totalFrames.value) {
    framesReady.value = true;
  }
};

const onFrameError = (index) => {
  frameErrors.value.add(index);
  console.error(`Failed to load frame ${index}`);
};

const updateCurrentFrame = (index) => {
  const clampedIndex = Math.max(0, Math.min(totalFrames.value - 1, index));
  if (clampedIndex !== currentFrameIndex.value) {
    currentFrameIndex.value = clampedIndex;
    currentFrameUrl.value = outputFramesUrls.value[clampedIndex];
  }
};

// Load frames in parallel
const loadFramesInParallel = () => {
  if (!outputFramesUrls.value || outputFramesUrls.value.length === 0) return;
  
  loadingPromises.value = [];
  
  const promises = outputFramesUrls.value.map((url, index) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        loadedFrames.value.add(index);
        loadedFramesCount.value = loadedFrames.value.size;
        resolve();
      };
      img.onerror = () => {
        frameErrors.value.add(index);
        console.error(`Failed to load frame ${index}`);
        reject();
      };
      img.src = url;
    });
  });
  
  loadingPromises.value = promises;
  
  Promise.allSettled(promises).then(() => {
    if (loadedFramesCount.value === totalFrames.value) {
      framesReady.value = true;
    }
  });
};

// Watch for outputFramesUrls changes
watch(outputFramesUrls, (newUrls) => {
  console.log(outputFramesUrls.value?.length);
  if (newUrls && newUrls.length > 0) {
    totalFrames.value = newUrls.length;
    currentFrameUrl.value = newUrls[0];
    currentFrameIndex.value = 0;
    loadedFramesCount.value = 0;
    framesReady.value = false;
    loadedFrames.value.clear();
    frameErrors.value.clear();
    
    console.log(`Initialized with ${totalFrames.value} frames`);
    loadFramesInParallel();
  }
}, { immediate: true });

// Enhanced square slider interaction
const startDragging = (e) => {
  e.preventDefault();
  isDragging.value = true;
  handleDrag(e);
  
  if (e.type === 'mousedown') {
    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', stopDragging);
  } else if (e.type === 'touchstart') {
    document.addEventListener('touchmove', handleDrag, { passive: false });
    document.addEventListener('touchend', stopDragging);
  }
};

const handleDrag = (e) => {
  if (!isDragging.value || !framesReady.value || !sliderContainer.value) return;
  
  e.preventDefault();
  
  const now = performance.now();
  if (now - lastUpdateTime.value < UPDATE_THROTTLE) return;
  lastUpdateTime.value = now;
  
  const container = sliderContainer.value;
  const rect = container.getBoundingClientRect();
  
  const clientX = e.clientX || (e.touches && e.touches[0].clientX);
  const clientY = e.clientY || (e.touches && e.touches[0].clientY);
  
  const xPercent = Math.max(0.01, Math.min(99, ((clientX - rect.left) / rect.width) * 100));
  const yPercent = Math.max(0.01, Math.min(99, ((clientY - rect.top) / rect.height) * 100));
  
  sliderPosition.value = { x: xPercent, y: yPercent };
  
  const frameIndex = Math.floor((xPercent / 100) * (totalFrames.value - 1));
  console.log(frameIndex);
  updateCurrentFrame(frameIndex);
};

const stopDragging = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDragging);
  document.removeEventListener('touchmove', handleDrag);
  document.removeEventListener('touchend', stopDragging);
};

// Linear slider for mobile
const startLinearDragging = (e) => {
  e.preventDefault();
  isDragging.value = true;
  handleLinearDrag(e);
  
  if (e.type === 'mousedown') {
    document.addEventListener('mousemove', handleLinearDrag);
    document.addEventListener('mouseup', stopLinearDragging);
  } else if (e.type === 'touchstart') {
    document.addEventListener('touchmove', handleLinearDrag, { passive: false });
    document.addEventListener('touchend', stopLinearDragging);
  }
};

const handleLinearDrag = (e) => {
  if (!isDragging.value || !framesReady.value) return;
  
  e.preventDefault();
  
  const now = performance.now();
  if (now - lastUpdateTime.value < UPDATE_THROTTLE) return;
  lastUpdateTime.value = now;
  
  const slider = e.target.closest('.relative');
  if (!slider) return;
  
  const rect = slider.getBoundingClientRect();
  const x = (e.clientX || (e.touches && e.touches[0].clientX)) - rect.left;
  const percentage = Math.max(0, Math.min(99, (x / rect.width) * 100));
  
  const frameIndex = Math.floor((percentage / 100) * (totalFrames.value - 1));
  updateCurrentFrame(frameIndex);
};

const stopLinearDragging = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleLinearDrag);
  document.removeEventListener('mouseup', stopLinearDragging);
  document.removeEventListener('touchmove', handleLinearDrag);
  document.removeEventListener('touchend', stopLinearDragging);
};

// Handle checkout process for paid plans
const handleCheckout = async (productType) => {
  if (!isAuthenticated.value) {
    handleFreeTier();
    return;
  }
  try {
    activeCheckoutProductType.value = productType;
    await createCheckout(productType);
  } catch (err) {
    console.error('Error creating checkout:', err);
  }
};

// Handle free tier signup
const handleFreeTier = () => {
  navigateTo('/login');
};

// Check device performance on mount
onMounted(() => {
  isMobile.value = window.innerWidth < 768;
  
  const start = performance.now();
  let count = 0;
  for (let i = 0; i < 1000000; i++) {
    count++;
  }
  const end = performance.now();
  isLowEndDevice.value = end - start > 100;

  if (route.query.section === 'pricing') {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      setTimeout(() => {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDragging);
  document.removeEventListener('touchmove', handleDrag);
  document.removeEventListener('touchend', stopDragging);
  document.removeEventListener('mousemove', handleLinearDrag);
  document.removeEventListener('mouseup', stopLinearDragging);
  document.removeEventListener('touchmove', handleLinearDrag);
  document.removeEventListener('touchend', stopLinearDragging);
});

// FAQ state
const openFaq = ref(null);

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index;
};

definePageMeta({
  layout: 'landing'
});

// Add SEO metadata
useHead({
  title: 'Ploppin - Transform Images into 3D Videos',
  meta: [
    { 
      name: 'description', 
      content: 'Turn your manga, selfies, portraits, and any images into immersive IRL experiences.' 
    },
    { property: 'og:title', content: 'Ploppin - Transform Images into 3D Videos' },
    { property: 'og:description', content: 'Turn your manga, selfies, portraits, and any images into immersive IRL experiences.' },
    { property: 'og:image', content: '/social-card.png' },
    { property: 'og:url', content: 'https://ploppin.com' },
    { name: 'twitter:title', content: 'Ploppin - Transform Images into 3D Videos' },
    { name: 'twitter:description', content: 'Turn your manga, selfies, portraits, and any images into immersive IRL experiences.' },
    { name: 'twitter:image', content: '/social-card.png' }
  ]
});
</script>

<style>
/* Additional custom styles */

/* Custom animations */
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

.float {
  animation: float 6s ease-in-out infinite;
}

/* Noise texture overlay */
.noise-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('data:image/svg+xml;charset=utf-8,<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><filter id="a"><feTurbulence type="fractalNoise" baseFrequency=".005" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter><rect width="100%" height="100%" filter="url(%23a)" opacity=".15"/></svg>');
  pointer-events: none;
  z-index: 100;
  opacity: 0.03;
}

/* Add loading animation */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.animate-pulse {
  background: linear-gradient(90deg, 
    rgba(255,255,255,0.03) 25%, 
    rgba(255,255,255,0.05) 50%, 
    rgba(255,255,255,0.03) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

/* Add responsive styles for slider */
@media (max-width: 768px) {
  .slider-container {
    width: 16rem; /* 256px */
  }
}
</style>